import * as Sentry from '@sentry/react'
import type { GraphQLFormattedError } from 'graphql'

/**
 * Qué errores GraphQL del gateway llegan a Sentry como `error` y cuáles no.
 *
 * Los códigos de abajo son respuestas de NEGOCIO: el gateway las devuelve a
 * propósito y la UI ya se las enseña al usuario (mensaje, formulario, pantalla
 * vacía). No son fallos del portal, así que reportarlas como `error` solo mete
 * ruido en Sentry. Se dejan como miga (`breadcrumb`) para que, si luego salta
 * un error real, el rastro de lo anterior siga disponible.
 *
 * El filtro vive aquí y no en el `beforeSend` de `sentry/init.ts`: allí solo se
 * podría leer el `extra` ya armado y filtrar por el texto del mensaje, que está
 * prohibido. Aquí se decide con el `extensions.code` de cada error.
 *
 * «Cuenta de organización sin rol» cae aquí por ser `FORBIDDEN`. Medirla como
 * `warning` es otro goal.
 *
 * Ante la duda, error: un código ausente, no string o desconocido se reporta.
 */
export const CODIGOS_DE_NEGOCIO: ReadonlySet<string> = new Set([
  // El usuario no tiene permiso: autorización, no un fallo del portal.
  'FORBIDDEN',
  // Entrada inválida del usuario: la UI la pinta junto al campo.
  'BAD_USER_INPUT',
  // El recurso no existe: la UI muestra su estado vacío o de «no encontrado».
  'NOT_FOUND',
  // Choque con el estado actual (p. ej. ya existe): la UI lo explica.
  'CONFLICT',
  // Límite de peticiones: la UI pide esperar y reintentar.
  'RATE_LIMITED',
])

/**
 * `true` solo si `extensions.code` es un string de `CODIGOS_DE_NEGOCIO`.
 * `INTERNAL_SERVER_ERROR`, `SERVICE_UNAVAILABLE`, sin código o con un código
 * que no es string dan `false`.
 */
export function esErrorDeNegocio(err: GraphQLFormattedError): boolean {
  const code = err.extensions?.['code']
  return typeof code === 'string' && CODIGOS_DE_NEGOCIO.has(code)
}

/**
 * Reporta los errores GraphQL de una respuesta: los de negocio quedan como
 * miga `info`; el resto va a Sentry como `error`, igual que antes.
 */
export function reportarErroresGraphQL(
  errors: ReadonlyArray<GraphQLFormattedError>,
  operationName: string | undefined,
): void {
  const operation = operationName ?? 'unknown'
  errors.forEach((err) => {
    if (esErrorDeNegocio(err)) {
      const code = err.extensions?.['code'] as string
      Sentry.addBreadcrumb({
        category: 'graphql',
        level: 'info',
        message: `GraphQL ${code}: ${err.message}`,
        data: { operation, code, path: err.path },
      })
      return
    }
    Sentry.captureMessage(`GraphQL error: ${err.message}`, {
      level: 'error',
      tags: { operation },
      extra: { path: err.path, extensions: err.extensions },
    })
  })
}
